import { Box, Button, Image, Modal, Typography } from "@drone-js/core";
import { HStack, VStack } from "apps/commons/components/Stack";
import { usePropertySelectionViewModelValue } from "apps/propertySelection/propertySelectionViewModelAtom";
import type { ExtendedScrapingErrorResponse } from "libs/codegen/property-onboarding";
import { memo } from "react";
import { useLocation } from "react-router";

interface Props {
    open: boolean;
    onClose: () => void;
}

const PropertyCard = () => {
    const {
        inputUrlCms: { viewOnAgodaAction },
    } = usePropertySelectionViewModelValue();
    const { state } = useLocation();
    const propertyItem = (state?.errorResponse as ExtendedScrapingErrorResponse)?.propertyItem;

    if (!propertyItem) return null;

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            alignItems="start"
            backgroundColor="base.inset"
            borderRadius="16"
            p="16"
            qM={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
            <HStack gap="12">
                <Image
                    src={propertyItem.image}
                    alt={propertyItem.name}
                    borderRadius="8"
                    width="48px"
                    height="48px"
                    objectFit="cover"
                />
                <VStack gap="8" justifyContent="center">
                    <Typography variant="body-default-strong" textColor="base.default">
                        {propertyItem.name}
                    </Typography>
                    <HStack>
                        <Typography variant="caption-default" textColor="feedback.positive">
                            Active
                        </Typography>
                        <Typography variant="caption-default" textColor="base.subtle">
                            &nbsp;· {propertyItem.id} · {propertyItem.location}
                        </Typography>
                    </HStack>
                </VStack>
            </HStack>
            <Box width="100%" pt="12" qM={{ width: "auto", pt: "0" }}>
                <Button
                    variant="label"
                    hierarchy="tertiary"
                    label={viewOnAgodaAction}
                    trailingIcon="fill.arrow.newpage"
                    href={`https://www.agoda.com/pages/agoda/default/page_hotel.aspx?hotel_id=${propertyItem.id}`}
                    target="_blank"
                    width="parent-fit"
                />
            </Box>
        </Box>
    );
};

export const DuplicatedModal = memo<Props>(({ open, onClose }) => {
    const {
        inputUrlCms: { propertyAlreadyListedOnAgoda, contactSupportAction, dismissAction, contactSupportForAccessInfo },
    } = usePropertySelectionViewModelValue();

    return (
        <Modal
            size="m"
            visible={open}
            onClose={onClose}
            title={propertyAlreadyListedOnAgoda}
            actions={[
                {
                    actionType: "button",
                    variant: "label",
                    hierarchy: "primary",
                    label: contactSupportAction,
                    href: "https://partnerhub.agoda.com/contacting-us-for-support/",
                    trailingIcon: "fill.arrow.newpage",
                    target: "_blank",
                },
                {
                    actionType: "button",
                    variant: "label",
                    hierarchy: "secondary",
                    label: dismissAction,
                    onClick: onClose,
                },
            ]}
        >
            <VStack gap="8">
                <PropertyCard />
                <Typography variant="body-s">{contactSupportForAccessInfo}</Typography>
            </VStack>
        </Modal>
    );
});
